<script setup>
const entriesStore = useEntriesStore();

const props = defineProps({
  type: String,
  detail: {
    type: Boolean,
    default: false,
  },
  size: String,
  title: String,
  subtitle: String,
  image: Object,
  video: String,
  url: String,
  slug: String,
  fade: Boolean,
  bgColor: String,
  active: {
    type: Boolean,
    default: false,
  },
});

const config = useRuntimeConfig();
const constants = useConstants();

async function close() {
  document.body.style.minHeight = "";
  await navigateTo("/");
  entriesStore.setCurrent(null);
}
</script>

<template>
  <div
    :id="slug"
    class="tile relative group"
    :class="[
      'tile--' + slug,
      'tile--' + size,
      active ? 'tile--active z-2' : '',
    ]"
  >
    <div
      v-if="active"
      class="tile--outline absolute inset-0 bottom-[10px] bg-white z-0"
    ></div>
    <NuxtLink
      :to="detail ? constants.routes.public.projects + slug : null"
      class="tile__inner absolute inset-[10px] overflow-hidden z-1"
      :style="[bgColor ? { backgroundColor: bgColor } : {}]"
    >
      <span v-if="type === 'image'" class="tile__image absolute inset-0 block">
        <picture v-if="image">
          <source
            v-if="image?.url?.webp"
            :srcset="image?.url?.webp"
            type="image/jpeg"
          />
          <source
            v-if="image?.url?.jpg"
            :srcset="image?.url?.jpg"
            type="image/webp"
          />
          <img
            class="block h-full w-full object-cover object-center transition duration-500 group-hover:scale-105"
            :src="image?.url?.jpg"
            :alt="image?.alt"
            :title="image?.title"
          />
        </picture>
      </span>
      <span v-if="type === 'video'" class="tile__video absolute inset-0 block">
        <video
          autoplay
          loop
          muted
          playsinline
          class="absolute inset-0 w-full h-full object-cover object-center"
        >
          <source :src="video" type="video/mp4" />
        </video>
      </span>
      <span
        v-if="fade"
        class="absolute left-0 bottom-0 right-0 h-1/3 max-h-[100px] bg-gradient-to-t from-black-opac-dark to-transparent"
      ></span>
      <span
        class="tile__content flex flex-col w-fit absolute bottom-0 pl-md pt-md pb-xs text-white pr-2xl"
      >
        <h2
          v-if="title"
          class="h3 inline px-[8px] py-[2px] bg-black font-bold md:text-base lg:text-xl xl:text-2xl w-fit mb-xxs"
          style="box-decoration-break: clone; -webkit-box-decoration-break: clone;"
        >
          {{ title }}
        </h2>
        <span v-if="subtitle" 
        class="inline px-[8px] py-[2px] bg-black w-fit"
        style="box-decoration-break: clone; -webkit-box-decoration-break: clone;">{{ subtitle }}</span>
      </span>
    </NuxtLink>
    <button
      v-if="active && detail"
      @click="close"
      class="block absolute right-[10px] bottom-[10px] z-1"
    >
      <span class="tile__more block">
        <span
          class="absolute bottom-0 right-0 w-[65px] lg:w-80px h-0 text-black transition-all duration-300 group-hover:text-red lg:group-hover:w-[100px] flex justify-center"
        >
          <IconClose class="mt-xxs lg:mt-xs" size="30px" />
        </span>
      </span>
    </button>

    <NuxtLink
      v-if="!active && detail"
      :to="detail ? constants.routes.public.projects + slug : null"
      class="block absolute right-[10px] bottom-[10px] z-1"
    >
      <span class="tile__more block">
        <span
          class="absolute bottom-0 right-0 w-[65px] lg:w-80px h-0 text-black transition-all duration-300 group-hover:text-red lg:group-hover:w-[100px] flex justify-center"
        >
          <IconAdd class="mt-xxs lg:mt-xs" size="30px" />
        </span>
      </span>
    </NuxtLink>
  </div>
</template>

<style>
.tile {
  &--2x2 {
    @apply w-full h-full pt-100p md:pt-0;
  }

  &--2x1 {
    @apply w-full h-1/2 pt-[50%] md:pt-0;
  }

  &--1x1 {
    @apply w-full h-full md:w-1/2 md:h-1/2 pt-100p md:pt-0;
  }

  &--outline {
    width: calc(100% + 20px);
    height: 100%;
    z-index: 0;
    position: absolute;
    left: -10px;
    top: -10px;
  }

  &__more {
    > span {
      border-bottom: 40px solid #fff;
      border-left: 20px solid transparent;

      @media (min-width: 1024px) {
        border-bottom: 50px solid #fff;
        border-left: 25px solid transparent;
      }
    }
  }

  &--empty {
    @apply pt-0 h-0;
  }
}
</style>
